import { z } from 'zod';
import { ModuleManifest } from '@momentum/shared';

/**
 * DISPOSABLE fixture. Proves: private suggestions → simultaneous server-phased reveal → discussion →
 * one authorised commit → deterministic outcome → result. Not the finished huddle encounter.
 */
export const POC_CHOICE_ID = 'poc-choice';

export const pocChoiceManifest = ModuleManifest.parse({
  id: POC_CHOICE_ID,
  version: '0.1.0',
  name: 'Choice test (disposable)',
  description: 'Private pick, timed reveal, open discussion, single authorised commit, deterministic outcome.',
  disposable: true,
  players: { min: 2, max: 6 },
  participation: 'cooperative',
  teams: null,
  voice: { scope: 'shared' },
  capabilities: { launchesChildren: false, childModuleIds: [], usesRandom: false, hasPrivateData: true, renderer: 'poc-choice' },
  launchableFromLobby: true,
});

export const PocChoiceConfig = z.object({
  /** Fixed steps; each step has four options and one decider. */
  steps: z.number().int().min(1).max(5).default(2),
  thinkMs: z.number().int().min(500).max(60_000).default(5_000),
  discussMs: z.number().int().min(1_000).max(120_000).default(15_000),
  resolvedMs: z.number().int().min(500).max(30_000).default(3_000),
  /** Who commits: rotate through participants per step, or always the first participant. */
  decider: z.enum(['rotate', 'first']).default('rotate'),
});
export type PocChoiceConfig = z.infer<typeof PocChoiceConfig>;

export const PocChoiceAction = z.discriminatedUnion('type', [
  /** Private during `think`, visible during `discuss`. Any assigned participant. */
  z.object({ type: z.literal('suggest'), optionId: z.string() }),
  /** Decider only, `discuss` phase only. Exactly one accepted per step. */
  z.object({ type: z.literal('commit'), optionId: z.string() }),
]);
export type PocChoiceAction = z.infer<typeof PocChoiceAction>;

export const PocChoiceOption = z.object({ id: z.string(), label: z.string() });

/** Projection. `suggestions` contains only the viewer's own entry during `think`; everyone's during `discuss`+. */
export const PocChoiceView = z.object({
  step: z.number().int(),
  steps: z.number().int(),
  phase: z.enum(['think', 'discuss', 'resolved', 'complete']),
  prompt: z.string(),
  options: z.array(PocChoiceOption),
  deciderId: z.string(),
  suggestions: z.record(z.string(), z.string()),
  /** Number of participants who have suggested (safe to show spectators during think). */
  suggestedCount: z.number().int(),
  mySuggestion: z.string().nullable(),
  canCommit: z.boolean(),
  committed: z.array(
    z.object({ step: z.number().int(), optionId: z.string().nullable(), byParticipantId: z.string(), timedOut: z.boolean() }),
  ),
  facts: z.array(z.string()),
  outcome: z.string().nullable(),
});
export type PocChoiceView = z.infer<typeof PocChoiceView>;

/** Result payload, schema version 1. A test counter for the parent; no score, no winner. */
export const PocChoiceResultPayload = z.object({
  schema: z.literal(1),
  path: z.array(z.object({ step: z.number().int(), optionId: z.string().nullable(), byParticipantId: z.string(), timedOut: z.boolean() })),
  stepsCompleted: z.number().int(),
  outcomeKey: z.string(),
});
export type PocChoiceResultPayload = z.infer<typeof PocChoiceResultPayload>;
